/*
 * Copyright 2025 Intuition Robotics ltd
 */
import React from 'react';
import {useState, useEffect} from "react";
import Cropper from "react-easy-crop";
import {Area} from "react-easy-crop";
import Button from "@components/Button";
import getCroppedImg from "@util/ImageUtils"
import '../css/style.css';
import toast, {Toaster} from 'react-hot-toast';
import {t} from "@util/i18n";

type Props = {
    originalImage: string;
    onClose: () => void;
    addCroppedImage: (image: File) => void;
};


const ImageCropperScreen = ({originalImage, onClose, addCroppedImage}: Props) => {
    const [crop, setCrop] = useState({x: 0, y: 0})
    const [zoom, setZoom] = useState(1)
    const [cropArea, setCropArea] = useState<Area>({x: 0, y: 0, width: 0, height: 0})
    const [showAdjustText, setShowAdjustText] = useState(true);
    const [isConfirmDisabled, setIsConfirmDisabled] = useState(false);

    useEffect(() => {
        const handleTouch = () => {
            setShowAdjustText(false);
        };

        document.addEventListener('touchstart', handleTouch);

        return () => {
            document.removeEventListener('touchstart', handleTouch);
        };
    }, []);


    const handleConfirm = async () => {
        setIsConfirmDisabled(true);

        const cropPromise = toast.promise(
            getCroppedImg({
                originalImage,
                crop: cropArea
            }),
            {
                loading: t("cropping"),
                success: t("cropSuccess"),
                error: (error) => `${t("cropFailed")} ${error.message}`
            }
        );

        cropPromise
            .then((result) => {
                addCroppedImage(result);
            })
            .catch((error) => console.error(error))
            .finally(() => onClose());
    };

    const onCropComplete = (croppedArea: Area, croppedAreaPixels: Area) => {
        setCropArea(croppedAreaPixels)
    }

    return (
        <div className='cropperScreen'>
            <div className='cropperContainer'>
                <Cropper
                    image={originalImage}
                    zoom={zoom}
                    aspect={5 / 8}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                    onCropChange={setCrop}
                    crop={crop}/>
            </div>

            {showAdjustText && (
                <div className='adjustImageText'>
                    <h2 className='baseText'>{t("cropperTitle")}</h2>
                </div>
            )}
            <div className='cropperBtn'>
                <Button onClick={onClose}>{t("cancel")}</Button>
                <Button onClick={handleConfirm} disabled={isConfirmDisabled}>{t("save")}</Button>
            </div>
            <Toaster toastOptions={{className: 'baseText'}}/>
        </div>
    )
}

export default ImageCropperScreen;